/**
 * Client HTTP unique de l'application.
 *
 * Responsabilités : préfixe de l'API, contexte d'édition (X-Event-Id), langue,
 * jeton d'authentification, et surtout traduction de l'enveloppe d'erreur
 * du backend en une exception typée exploitable par l'UI.
 *
 * Aucun composant ne doit appeler `fetch` directement.
 */

const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/api/v1'

export interface ApiErrorDetail {
  field?: string
  reason?: string
  message?: string
  [key: string]: unknown
}

/** Codes métier stables renvoyés par le backend (cf. docs/03-API.md). */
export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'PRICE_CHANGED'
  | 'QUOTE_EXPIRED'
  | 'SOLD_OUT'
  | 'INSUFFICIENT_STOCK'
  | 'TABLE_ALREADY_RESERVED'
  | 'ALREADY_CHECKED_IN'
  | 'ALREADY_VOTED'
  | 'VOTING_CLOSED'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'
  | (string & {})

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly details: ApiErrorDetail[]
  readonly requestId: string | null

  constructor(args: {
    code: ApiErrorCode
    message: string
    status: number
    details?: ApiErrorDetail[]
    requestId?: string | null
  }) {
    super(args.message)
    this.name = 'ApiError'
    this.code = args.code
    this.status = args.status
    this.details = args.details ?? []
    this.requestId = args.requestId ?? null
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Contexte d'édition — requis par les surfaces admin et ops. */
  eventId?: string
  /** Clé d'idempotence — obligatoire à la création de commande. */
  idempotencyKey?: string
  locale?: string
  token?: string
  /**
   * Statuts non-2xx dont la réponse est une donnée métier, pas une erreur.
   *
   * Le check-in en est le seul cas : « déjà entré » (409) ou « impayé » (402)
   * sont des réponses attendues, que le staff doit lire en détail. Elles ont
   * un corps typé, pas l'enveloppe d'erreur.
   */
  acceptStatuses?: number[]
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, eventId, idempotencyKey, locale, token, acceptStatuses, headers, ...rest } = options

  // Un fichier ne se sérialise pas en JSON, et poser `Content-Type` soi-même
  // sur un envoi multipart casse la requête : le navigateur doit y ajouter la
  // frontière (« boundary ») qu'il génère. On le laisse donc faire.
  const isUpload = body instanceof FormData

  const finalHeaders = new Headers(headers)
  finalHeaders.set('Accept', 'application/json')
  if (body !== undefined && !isUpload) finalHeaders.set('Content-Type', 'application/json')
  if (eventId) finalHeaders.set('X-Event-Id', eventId)
  if (idempotencyKey) finalHeaders.set('Idempotency-Key', idempotencyKey)
  if (locale) finalHeaders.set('Accept-Language', locale)
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`)

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...rest,
    headers: finalHeaders,
    ...(body !== undefined ? { body: isUpload ? (body as FormData) : JSON.stringify(body) } : {}),
  })

  if (response.status === 204) return undefined as T

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok && !acceptStatuses?.includes(response.status)) {
    const envelope =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as { error: { code: string; message: string; details?: ApiErrorDetail[]; request_id?: string } }).error
        : null

    throw new ApiError({
      code: envelope?.code ?? 'INTERNAL_ERROR',
      message: envelope?.message ?? `Erreur ${response.status}`,
      status: response.status,
      details: envelope?.details ?? [],
      requestId: envelope?.request_id ?? response.headers.get('X-Request-Id'),
    })
  }

  return payload as T
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
